import React from 'react'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { sdk } from '@/lib/sdk'
import { NOTIFICATION_EVENT, useRealtime, useRealtimeContext, useRealtimeEvents, type RealtimeEvent } from '@/lib/realtime/RealtimeProvider'
import { chatSoundsEnabled, installChatSounds, playReceiveSound, playSendSound, setChatSoundsEnabled } from './chatSounds'
import { createMeSyncGate, ME_SYNC_DEBOUNCE_MS } from './chatMeSync'
import { AWAY_AFTER_MS } from './chatStatus'
import type {
  ChatConnectionState,
  ChatConversation,
  ChatMe,
  ChatMessage,
  ChatPanelState,
  ChatPresenceStatus,
  ChatProfile,
  ChatShownStatus,
  ChatStatus as MyChatStatus
} from '@/lib/sdk-official/resources/chat'

/** F-184: the person's own status; an API without `chatStatus` only knows available or not. */
export const myChatStatusOf = (me: Pick<ChatMe, 'chatAvailable' | 'chatStatus'> | null | undefined): MyChatStatus =>
  me?.chatStatus ?? (me?.chatAvailable ? 'AVAILABLE' : 'UNAVAILABLE')

/**
 * F-108 Mensagens: the chat's state for the whole shell (panel, header button,
 * profile card, `/_/chat/{id}` link). F-110: the events come from the
 * platform socket (RealtimeProvider); while it is down, the routes are polled
 * on the slow fallback.
 */

/** Open windows kept on the desktop dock; the ones that do not fit go to the "+N" menu. */
export const MAX_DOCKED_WINDOWS = 8
/** Window events: a connection changed (`{ connectionId, profileId, connection }`), a notification arrived or changed. */
export const CHAT_CONNECTION_EVENT = 'memberfy:chat-connection'
/** A `presence.delta` from the socket (`{ profileId, online, profile? }`): the People panel patches its rows. */
export const CHAT_PRESENCE_EVENT = 'memberfy:chat-presence'
export { NOTIFICATION_EVENT }
/** Opens the header's chat panel on a tab: `detail.tab` is `conversations` or `people` (the `/_/chat/connections` link). */
export const OPEN_CHAT_EVENT = 'memberfy:open-chat'

/** `focus: false` shows the window without taking the cursor (windows put back on load, a refresh). */
/** `text` goes into an empty composer, unsent and editable (the "Fale com a equipe" draft, F-161b). */
export interface ShowOptions { focus?: boolean; text?: string }
export interface FocusRequest { id: string; seq: number; at: number; text?: string }
/** Opens the notifications bell's panel (connection requests are answered there). */
export const OPEN_NOTIFICATIONS_EVENT = 'memberfy:open-notifications'
const RETRY_FAILED_MS = 60_000
/** A socket `ready` right after a presence fetch does not fetch again. */
const PRESENCE_RESYNC_GAP_MS = 5_000
const TYPING_SHOWN_MS = 4_000
const TYPING_SEND_GAP_MS = 3_000
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface PendingMessage extends ChatMessage {
  /** Only on the sender's own copy while it is on its way, or when it failed. */
  sendState?: 'sending' | 'failed'
}

export const DRAFT_PREFIX = 'draft:'
export const isDraftId = (id: string) => id.startsWith(DRAFT_PREFIX)

/** A chat window with a person that has no conversation yet. */
export interface ChatDraft {
  id: string
  /** `canConverse`: the server said a chat may start without a connection (a shared Networking program, F-176 c.20). */
  profile: ChatProfile & { online?: boolean | null; canConverse?: boolean }
  connection: ChatConnectionState
}

export interface ConversationMessages {
  items: PendingMessage[]
  hasMore: boolean
  cursor: string | null
  state: 'loading' | 'ready' | 'failed'
  loadingOlder?: boolean
}

type ChatStatus = 'off' | 'loading' | 'ready' | 'disabled' | 'failed'

interface ChatContextValue {
  status: ChatStatus
  /** Chat on in the community and the person signed in: the panel shows. */
  enabled: boolean
  me: ChatMe | null
  myId: string | null
  connected: boolean
  conversations: ChatConversation[]
  conversationsState: 'loading' | 'ready' | 'failed'
  conversationsHasMore: boolean
  loadingMoreConversations: boolean
  loadMoreConversations: () => void
  online: ChatProfile[]
  /** Available people online now (`pagination.online` of `/chat/members`, then kept by `presence.delta`); null when unknown. */
  onlineTotal: number | null
  onlineState: 'idle' | 'loading' | 'ready' | 'failed'
  panelState: ChatPanelState
  /** `persist: false` changes it for this page only (a full-screen editor folding the dock), keeping the person's saved choice. */
  setPanelState: (state: ChatPanelState, opts?: { persist?: boolean }) => void
  /** Conversations open as windows (desktop) or the one on screen (side panel, mobile). */
  activeIds: string[]
  mobileOpen: boolean
  setMobileOpen: (open: boolean) => void
  /** F-184: the avatar's chat panel is open (the docked windows sit to its left while it is). */
  sidePanelOpen: boolean
  setSidePanelOpen: (open: boolean) => void
  messages: Record<string, ConversationMessages>
  typing: Record<string, number>
  syncTick: number
  refresh: () => Promise<void>
  refreshConversations: () => Promise<void>
  refreshOnline: () => Promise<void>
  updateMe: (patch: Partial<Pick<ChatMe, 'chatAvailable' | 'chatReadReceipts' | 'chatShowLastSeen' | 'availabilityNoticeSeen'>>) => Promise<void>
  /** F-184: my status (derived from `chatAvailable` on an older API). */
  myStatus: MyChatStatus
  /** What my dot shows: `myStatus`, or AWAY while this tab has been idle for 10 min on top of AVAILABLE. */
  myShownStatus: ChatShownStatus
  /** Changes my status at once and saves it; on an API error it goes back and the error is rethrown. */
  setChatStatus: (next: MyChatStatus) => Promise<void>
  /** Opens (or creates) the conversation with a person and shows it. Throws the API error. */
  /**
   * Shows the chat with a person. With no conversation yet it opens a draft window (`draft:<profileId>`):
   * nothing is created until the first message is sent.
   */
  openWith: (profileId: string, profile?: ChatDraft['profile'], connection?: ChatConnectionState, opts?: ShowOptions) => Promise<string>
  drafts: Record<string, ChatDraft>
  /** Sends the first message of a draft: creates the conversation, swaps the window to it, sends. */
  sendFirstMessage: (draftId: string, body: string) => Promise<void>
  /** "Limpar histórico": clears the conversation for me only. */
  clearHistory: (conversationId: string) => Promise<void>
  /** Shows a conversation already known by id (the e-mail link). Throws the API error. */
  showConversation: (conversationId: string, opts?: ShowOptions) => Promise<void>
  /** Shows a conversation the API just returned (e.g. `POST /chat/contact-team`). */
  showReturnedConversation: (conversation: ChatConversation, opts?: ShowOptions) => void
  /** The last window someone asked to type in; the view focuses its composer once (`seq`), if it can send. */
  focusRequest: FocusRequest | null
  /** Asks a window already on screen for the cursor (a minimized window restored). */
  requestFocus: (conversationId: string, text?: string) => void
  /** Brings a window already open to the first slot, with the cursor (the dock's "+N" menu). */
  showWindow: (conversationId: string) => void
  /** Chat sounds on this browser for me ("Sons" in the settings; on by default). */
  soundsOn: boolean
  setSoundsOn: (on: boolean) => void
  /** The open view reports whether the person is reading the bottom (no receive sound then). */
  reportAtBottom: (conversationId: string, atBottom: boolean) => void
  closeConversation: (conversationId: string) => void
  loadMessages: (conversationId: string, opts?: { older?: boolean; silent?: boolean }) => Promise<void>
  sendMessage: (conversationId: string, body: string, retryOf?: string) => Promise<void>
  deleteMessage: (conversationId: string, messageId: string) => Promise<void>
  markRead: (conversationId: string) => void
  sendTyping: (conversationId: string) => void
  setBlocked: (profileId: string, blocked: boolean) => Promise<void>
  /** Views on screen register here, so a new message there is read, not counted. */
  registerVisible: (conversationId: string) => () => void
  /** Whether "Enviar mensagem" makes sense for this person (available and reachable). */
  canMessage: (profileId: string, hint?: { chatAvailable?: boolean | null }) => boolean
  isOnline: (profileId: string) => boolean
}

const ChatContext = React.createContext<ChatContextValue | null>(null)

export const useChat = (): ChatContextValue => {
  const value = React.useContext(ChatContext)
  if (!value) throw new Error('useChat must be used within ChatProvider')
  return value
}

/** For components that may render outside the provider (they then hide their chat bits). */
export const useOptionalChat = (): ChatContextValue | null => React.useContext(ChatContext)

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number }; status?: number })?.response?.status ?? (err as { status?: number })?.status

const byLastActivity = (a: ChatConversation, b: ChatConversation) =>
  new Date(b.lastMessageAt || b.createdAt || 0).getTime() - new Date(a.lastMessageAt || a.createdAt || 0).getTime()

const mergeMessages = (current: PendingMessage[], incoming: PendingMessage[]): PendingMessage[] => {
  const map = new Map<string, PendingMessage>()
  for (const m of current) map.set(m.id, m)
  for (const m of incoming) map.set(m.id, { ...map.get(m.id), ...m, sendState: undefined })
  return [...map.values()].sort((a, b) => {
    /* Messages still on their way stay at the end, in the order they were written. */
    if (a.sendState && !b.sendState) return 1
    if (!a.sendState && b.sendState) return -1
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  })
}

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isRoleResolved } = useAuth()
  const { community } = useCommunity()
  const communityId = community?.id || null
  const myId = user?.id || null
  const sessionReady = Boolean(isAuthenticated && isRoleResolved && communityId && myId)

  const [status, setStatus] = React.useState<ChatStatus>('off')
  const [me, setMe] = React.useState<ChatMe | null>(null)
  const realtime = useRealtimeContext()
  const [conversations, setConversations] = React.useState<ChatConversation[]>([])
  const [conversationsState, setConversationsState] = React.useState<'loading' | 'ready' | 'failed'>('loading')
  const [online, setOnline] = React.useState<ChatProfile[]>([])
  const [onlineState, setOnlineState] = React.useState<'idle' | 'loading' | 'ready' | 'failed'>('idle')
  const [onlineTotal, setOnlineTotal] = React.useState<number | null>(null)
  const onlineIdsRef = React.useRef(new Set<string>())
  const presenceSyncedAtRef = React.useRef(0)
  const [panelState, setPanelStateLocal] = React.useState<ChatPanelState>('COLLAPSED')
  const [activeIds, setActiveIds] = React.useState<string[]>([])
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [sidePanelOpen, setSidePanelOpen] = React.useState(false)
  const [focusRequest, setFocusRequest] = React.useState<FocusRequest | null>(null)
  const requestFocus = React.useCallback((conversationId: string, text?: string) => {
    setFocusRequest((prev) => ({ id: conversationId, seq: (prev?.seq || 0) + 1, at: Date.now(), text }))
  }, [])
  const [messages, setMessages] = React.useState<Record<string, ConversationMessages>>({})
  const [typing, setTyping] = React.useState<Record<string, number>>({})
  const [syncTick, setSyncTick] = React.useState(0)
  const [drafts, setDrafts] = React.useState<Record<string, ChatDraft>>({})

  const visibleRef = React.useRef(new Map<string, number>())
  const atBottomRef = React.useRef(new Map<string, boolean>())
  const reportAtBottom = React.useCallback((conversationId: string, atBottom: boolean) => {
    atBottomRef.current.set(conversationId, atBottom)
  }, [])
  const activeIdsRef = React.useRef<string[]>([])
  const typingSentRef = React.useRef(new Map<string, number>())
  const conversationsRef = React.useRef(conversations)
  conversationsRef.current = conversations
  activeIdsRef.current = activeIds

  React.useEffect(() => { installChatSounds() }, [])
  const [soundsOn, setSoundsOnState] = React.useState(true)
  React.useEffect(() => { setSoundsOnState(chatSoundsEnabled(myId || null)) }, [myId])
  const setSoundsOn = React.useCallback((on: boolean) => {
    setChatSoundsEnabled(myId || null, on)
    setSoundsOnState(on)
  }, [myId])
  const soundsOnRef = React.useRef(soundsOn)
  soundsOnRef.current = soundsOn
  const messagesRef = React.useRef(messages)
  messagesRef.current = messages

  const enabled = status === 'ready'
  /* The chat is live only with the platform socket up and the chat on in the community. */
  const connected = enabled && realtime.connected && realtime.chatEnabled !== false

  /* The badge follows the server: stale `/chat/me` answers are dropped, reads resync it (APP-172). */
  const meGateRef = React.useRef(createMeSyncGate())
  const meSyncTimerRef = React.useRef<number | null>(null)
  const loadMeRef = React.useRef<() => Promise<void>>(async () => {})
  const scheduleMeSync = React.useCallback(() => {
    if (meSyncTimerRef.current !== null) window.clearTimeout(meSyncTimerRef.current)
    meSyncTimerRef.current = window.setTimeout(() => {
      meSyncTimerRef.current = null
      loadMeRef.current()
    }, ME_SYNC_DEBOUNCE_MS)
  }, [])
  React.useEffect(() => () => {
    if (meSyncTimerRef.current !== null) window.clearTimeout(meSyncTimerRef.current)
  }, [])

  /* Everything resets when the session or the community changes. */
  React.useEffect(() => {
    meGateRef.current.invalidate()
    if (meSyncTimerRef.current !== null) window.clearTimeout(meSyncTimerRef.current)
    meSyncTimerRef.current = null
    setMe(null)
    setConversations([])
    setOnline([])
    setOnlineTotal(null)
    onlineIdsRef.current = new Set()
    setOnlineState('idle')
    setMessages({})
    setActiveIds([])
    setTyping({})
    pagesLoadedRef.current = 1
    setDrafts({})
    setStatus(sessionReady ? 'loading' : 'off')
  }, [sessionReady, communityId, myId])

  const loadMe = React.useCallback(async () => {
    const token = meGateRef.current.begin()
    try {
      const loaded = await sdk.chat.getMe()
      /* Counted before a read this tab already applied (or older than one applied): ask again. */
      if (!meGateRef.current.accept(token)) {
        scheduleMeSync()
        return
      }
      setMe(loaded)
      if (loaded?.panelState) setPanelStateLocal(loaded.panelState)
      setStatus('ready')
    } catch (err) {
      const code = statusOf(err)
      if (!meGateRef.current.accept(token)) return
      setStatus(code === 403 || code === 404 ? 'disabled' : 'failed')
    }
  }, [scheduleMeSync])
  loadMeRef.current = loadMe

  /* Conversations come 30 at a time; the list asks for more as it scrolls (no "Ver mais"). */
  const [conversationsPage, setConversationsPage] = React.useState(1)
  const [conversationsHasMore, setConversationsHasMore] = React.useState(false)
  const [loadingMoreConversations, setLoadingMoreConversations] = React.useState(false)
  const pagesLoadedRef = React.useRef(1)

  const refreshConversations = React.useCallback(async () => {
    try {
      /* A refresh reads the newest page and keeps what was already scrolled through below it. */
      const page = await sdk.chat.listConversations({ limit: 30 })
      setConversations((previous) => {
        const fresh = new Set(page.items.map((c) => c.id))
        const older = pagesLoadedRef.current > 1 ? previous.filter((c) => !fresh.has(c.id)) : []
        return [...page.items, ...older].sort(byLastActivity)
      })
      if (pagesLoadedRef.current === 1) {
        const totalPages = page.pagination?.totalPages
        setConversationsHasMore(typeof totalPages === 'number' ? totalPages > 1 : page.items.length === 30)
        setConversationsPage(1)
      }
      setConversationsState('ready')
    } catch {
      setConversationsState((previous) => (previous === 'ready' ? previous : 'failed'))
    }
  }, [])

  const loadMoreConversations = React.useCallback(async () => {
    if (loadingMoreConversations || !conversationsHasMore) return
    setLoadingMoreConversations(true)
    try {
      const next = conversationsPage + 1
      const page = await sdk.chat.listConversations({ page: next, limit: 30 })
      pagesLoadedRef.current = next
      setConversations((list) => [...list, ...page.items.filter((c) => !list.some((x) => x.id === c.id))].sort(byLastActivity))
      setConversationsPage(next)
      setConversationsHasMore(typeof page.pagination?.totalPages === 'number' ? next < page.pagination.totalPages : page.items.length === 30)
    } catch {
      setConversationsHasMore(false)
    } finally {
      setLoadingMoreConversations(false)
    }
  }, [loadingMoreConversations, conversationsHasMore, conversationsPage])

  const refreshOnline = React.useCallback(async () => {
    setOnlineState((previous) => (previous === 'ready' ? previous : 'loading'))
    presenceSyncedAtRef.current = Date.now()
    try {
      /* The list (first 50) and the total; `presence.delta` keeps both current afterwards. */
      const [page, count] = await Promise.all([
        sdk.chat.listOnline({ limit: 50 }),
        sdk.chat.listPeople({ limit: 1 }).catch(() => null)
      ])
      const list = page.items.filter((p) => p.id !== myId)
      onlineIdsRef.current = new Set(list.map((p) => p.id))
      setOnline(list)
      setOnlineTotal(count?.onlineTotal ?? null)
      setOnlineState('ready')
    } catch (err) {
      if (statusOf(err) === 403) {
        setOnline([])
        setOnlineTotal(null)
        onlineIdsRef.current = new Set()
        setOnlineState('idle')
      } else {
        setOnlineState('failed')
      }
    }
  }, [myId])

  const refresh = React.useCallback(async () => {
    await loadMe()
    await refreshConversations()
  }, [loadMe, refreshConversations])

  /* First load, and a retry a minute later when the API could not be reached. */
  React.useEffect(() => {
    if (!sessionReady) return
    if (status === 'loading') {
      refresh()
      return
    }
    if (status === 'failed') {
      const timer = window.setTimeout(refresh, RETRY_FAILED_MS)
      return () => window.clearTimeout(timer)
    }
  }, [sessionReady, status, refresh])

  /*
   * "Online agora": only for a person who is available. One fetch when it turns on; then the
   * socket's presence.delta keeps it (and a resync on every socket `ready`). While the socket is
   * down, the slow fallback poll below refreshes it with the conversations.
   */
  const available = Boolean(me?.chatAvailable)
  React.useEffect(() => {
    if (!enabled || !available) {
      setOnline([])
      setOnlineTotal(null)
      onlineIdsRef.current = new Set()
      setOnlineState('idle')
      return
    }
    refreshOnline()
  }, [enabled, available, refreshOnline])

  /* Without the socket the routes are the source, on the platform's fallback (2 min visible, 5 min hidden).
     The socket's own `ready` resyncs after a reconnect, so only the fallback reason runs here. */
  useRealtime([], () => {
    loadMe()
    refreshConversations()
    setSyncTick((n) => n + 1)
    if (available) refreshOnline()
  }, { enabled, topics: [], reasons: ['fallback'] })

  const patchConversation = React.useCallback((id: string, patch: (c: ChatConversation) => ChatConversation) => {
    setConversations((list) => list.map((c) => (c.id === id ? patch(c) : c)))
  }, [])

  const isVisible = (conversationId: string) =>
    (visibleRef.current.get(conversationId) || 0) > 0 && document.visibilityState === 'visible'

  const markRead = React.useCallback((conversationId: string) => {
    const conversation = conversationsRef.current.find((c) => c.id === conversationId)
    const unread = conversation?.unreadCount || 0
    if (conversation && unread === 0) return
    /* The ref too, right away: the server's own "read" echo (onRead) must not subtract it again. */
    conversationsRef.current = conversationsRef.current.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
    patchConversation(conversationId, (c) => ({ ...c, unreadCount: 0 }))
    setMe((m) => (m ? {
      ...m,
      unreadTotal: Math.max(0, (m.unreadTotal || 0) - unread),
      unreadConversations: Math.max(0, (m.unreadConversations || 0) - (unread > 0 ? 1 : 0))
    } : m))
    meGateRef.current.invalidate()
    sdk.chat.markRead(conversationId).catch(() => {}).finally(scheduleMeSync)
  }, [patchConversation, scheduleMeSync])

  /* ---- socket events ---- */
  const onMessageNew = (message: ChatMessage) => {
    const mine = message.mine ?? message.senderId === myId
    const known = conversationsRef.current.some((c) => c.id === message.conversationId)
    /* F-184 Ocupado: the message only counts (badge, tab title); no sound and no window opening by itself. */
    const busy = myChatStatusOf(me) === 'BUSY' || message.silent === true
    if (!mine && me?.chatAvailable && !busy) {
      const id = message.conversationId
      /* Reading it right now (window on screen, this tab focused, at the bottom): no sound. */
      const reading = isVisible(id) && document.hasFocus() && atBottomRef.current.get(id) !== false
      if (!reading && soundsOnRef.current) playReceiveSound()
      /* Desktop dock, LinkedIn style: a message in a conversation that is not open opens its window,
         without taking the cursor. A minimized one stays minimized (only its badge grows). */
      if (window.innerWidth >= 1024 && !activeIdsRef.current.includes(id)) show(id, { focus: false })
    }
    setMessages((all) => {
      const current = all[message.conversationId]
      if (!current) return all
      /* My own copy from another tab, or the echo of the one this tab sent: merge by id. */
      return { ...all, [message.conversationId]: { ...current, items: mergeMessages(current.items, [{ ...message, mine }]) } }
    })
    if (!known) {
      refreshConversations()
      if (!mine) loadMe()
      else scheduleMeSync()
      return
    }
    /* Sending marks the conversation read on the server: take its total. */
    if (mine) scheduleMeSync()
    const seen = mine || isVisible(message.conversationId)
    setConversations((list) => list.map((c) => (c.id === message.conversationId
      ? { ...c, lastMessage: { ...message, mine }, lastMessageAt: message.createdAt, unreadCount: seen ? c.unreadCount || 0 : (c.unreadCount || 0) + 1 }
      : c)).sort(byLastActivity))
    if (!mine) {
      setTyping((t) => {
        if (!t[message.conversationId]) return t
        const next = { ...t }
        delete next[message.conversationId]
        return next
      })
      if (seen) {
        meGateRef.current.invalidate()
        sdk.chat.markRead(message.conversationId).catch(() => {}).finally(scheduleMeSync)
      } else {
        setMe((m) => (m ? { ...m, unreadTotal: (m.unreadTotal || 0) + 1 } : m))
      }
    }
  }

  const onMessageDeleted = (data: { id: string; conversationId: string }) => {
    setMessages((all) => {
      const current = all[data.conversationId]
      if (!current) return all
      return { ...all, [data.conversationId]: { ...current, items: current.items.map((m) => (m.id === data.id ? { ...m, deleted: true, body: null } : m)) } }
    })
    patchConversation(data.conversationId, (c) => (c.lastMessage?.id === data.id ? { ...c, lastMessage: { ...c.lastMessage, deleted: true, body: null } } : c))
  }

  const onRead = (data: { conversationId: string; profileId: string; readAt: string }) => {
    if (data.profileId === myId) {
      /* Read in another tab of mine. */
      const unread = conversationsRef.current.find((c) => c.id === data.conversationId)?.unreadCount || 0
      patchConversation(data.conversationId, (c) => ({ ...c, unreadCount: 0, lastReadAt: data.readAt }))
      if (unread) setMe((m) => (m ? { ...m, unreadTotal: Math.max(0, (m.unreadTotal || 0) - unread) } : m))
      /* The server already applied it: a `/chat/me` in flight is stale, and the total comes from it. */
      meGateRef.current.invalidate()
      scheduleMeSync()
    } else {
      patchConversation(data.conversationId, (c) => ({ ...c, otherLastReadAt: data.readAt }))
    }
  }

  const onPresence = (data: { profileId: string; online: boolean; status?: ChatPresenceStatus | null; lastSeenAt?: string | null; profile?: ChatProfile }) => {
    if (data.profileId === myId) return
    const known = onlineIdsRef.current
    /* F-184: a delta for someone already online is a status change (Disponível <-> Ocupado). */
    const status: ChatPresenceStatus | null = data.online ? data.status ?? data.profile?.status ?? 'AVAILABLE' : null
    if (data.online) {
      if (!known.has(data.profileId)) {
        setOnlineTotal((n) => (n === null ? n : n + 1))
        if (data.profile) known.add(data.profileId)
      }
      setOnline((list) => (list.some((p) => p.id === data.profileId)
        ? list.map((p) => (p.id === data.profileId ? { ...p, status } : p))
        : !data.profile
          ? list
          : [...list, { ...data.profile, status }].sort((a, b) => (a.name || '').localeCompare(b.name || ''))))
      if (!data.profile) refreshOnline()
    } else {
      /* Someone beyond the first 50 (not in the list) still counts while the total exceeds the list. */
      const wasKnown = known.delete(data.profileId)
      const tracked = known.size
      setOnlineTotal((n) => (n === null ? n : wasKnown || n > tracked ? Math.max(0, n - 1) : n))
      setOnline((list) => list.filter((p) => p.id !== data.profileId))
    }
    window.dispatchEvent(new CustomEvent(CHAT_PRESENCE_EVENT, { detail: data }))
    setConversations((list) => list.map((c) => (c.other?.id === data.profileId
      ? { ...c, other: { ...c.other, online: data.online, status, ...(data.online ? {} : { lastSeenAt: data.lastSeenAt ?? null }), available: data.online ? true : c.other.available } }
      : c)))
  }

  const onEvent = (event: RealtimeEvent) => {
    const data = (event?.data || {}) as Record<string, unknown>
    if (event?.type === 'chat.settings') {
      if (data.enabled === false) setStatus('disabled')
      else loadMe()
      return
    }
    if (event?.type === 'session.revoked') {
      /* The session behind the socket is gone: /chat/me answers 401 and the app signs out. */
      if (status !== 'off') loadMe()
      return
    }
    if (!enabled) return
    switch (event?.type) {
      case 'ready':
        if (data.chatEnabled === false) break
        if (typeof data.chatStatus === 'string') setMe((m) => (m ? { ...m, chatStatus: data.chatStatus as MyChatStatus } : m))
        refreshConversations()
        setSyncTick((n) => n + 1)
        /* Deltas missed while the socket was down: one resync (skipped right after the first fetch). */
        if (me?.chatAvailable && Date.now() - presenceSyncedAtRef.current > PRESENCE_RESYNC_GAP_MS) refreshOnline()
        break
      case 'message.new':
        onMessageNew(data as unknown as ChatMessage)
        break
      case 'message.deleted':
        onMessageDeleted(data as unknown as { id: string; conversationId: string })
        break
      case 'typing':
        if (typeof data.conversationId === 'string' && data.profileId !== myId) {
          setTyping((t) => ({ ...t, [data.conversationId as string]: Date.now() }))
        }
        break
      case 'read':
        onRead(data as unknown as { conversationId: string; profileId: string; readAt: string })
        break
      case 'presence.delta':
        onPresence(data as unknown as { profileId: string; online: boolean; status?: ChatPresenceStatus | null; lastSeenAt?: string | null; profile?: ChatProfile })
        break
      case 'conversation.blocked':
        refreshConversations()
        if (me?.chatAvailable) refreshOnline()
        break
      case 'me.updated':
        /* Another tab of mine changed the status (F-184: `chatStatus` too). */
        if (typeof data.chatAvailable === 'boolean') {
          const chatStatus = typeof data.chatStatus === 'string' ? data.chatStatus as MyChatStatus : undefined
          setMe((m) => (m ? { ...m, chatAvailable: data.chatAvailable as boolean, showUnreadBadge: data.chatAvailable as boolean, ...(chatStatus ? { chatStatus } : {}) } : m))
        }
        /* F-184: "Mostrar quando estive online por último" changed in another tab; the others' times follow it. */
        if (typeof data.chatShowLastSeen === 'boolean') {
          setMe((m) => (m ? { ...m, chatShowLastSeen: data.chatShowLastSeen as boolean } : m))
          refreshConversations()
        }
        break
      case 'connection.requested':
      case 'connection.accepted':
      case 'connection.removed':
        /* The conversation's connection state and the People panel follow. */
        refreshConversations()
        if (typeof data.profileId === 'string' && typeof data.connection === 'string') {
          const draftId = DRAFT_PREFIX + data.profileId
          setDrafts((all) => (all[draftId] ? { ...all, [draftId]: { ...all[draftId], connection: data.connection as ChatConnectionState } } : all))
        }
        window.dispatchEvent(new CustomEvent(CHAT_CONNECTION_EVENT, { detail: data }))
        break
      default:
        break
    }
  }
  /* Notifications and the Drop text are relayed by RealtimeProvider itself; the rest is the chat's. */
  useRealtimeEvents(onEvent)

  /* "digitando…" fades out on its own. */
  React.useEffect(() => {
    const keys = Object.keys(typing)
    if (!keys.length) return
    const timer = window.setTimeout(() => {
      const now = Date.now()
      setTyping((t) => Object.fromEntries(Object.entries(t).filter(([, at]) => now - at < TYPING_SHOWN_MS)))
    }, TYPING_SHOWN_MS)
    return () => window.clearTimeout(timer)
  }, [typing])

  const setPanelState = React.useCallback((next: ChatPanelState, opts?: { persist?: boolean }) => {
    setPanelStateLocal(next)
    if (opts?.persist === false) return
    setMe((m) => (m ? { ...m, panelState: next } : m))
    sdk.chat.updateMe({ panelState: next }).catch(() => {})
  }, [])

  const updateMe = React.useCallback(async (patch: Partial<Pick<ChatMe, 'chatAvailable' | 'chatReadReceipts' | 'chatShowLastSeen' | 'availabilityNoticeSeen'>>) => {
    const updated = await sdk.chat.updateMe(patch)
    setMe((m) => ({ ...(m || {}), ...updated } as ChatMe))
    if (patch.chatReadReceipts !== undefined || patch.chatShowLastSeen !== undefined) refreshConversations()
  }, [refreshConversations])

  const myStatus = myChatStatusOf(me)
  /* F-184 Ausente: automatic, from this tab's activity (mouse, keyboard, touch, scroll, the tab shown again). */
  const [idle, setIdle] = React.useState(false)
  React.useEffect(() => {
    if (!enabled) return
    let timer = 0
    let last = 0
    const arm = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setIdle(true), AWAY_AFTER_MS)
    }
    const onActivity = () => {
      setIdle(false)
      /* At most one re-arm per second: mousemove fires a lot. */
      const now = Date.now()
      if (now - last < 1000) return
      last = now
      arm()
    }
    const onVisibility = () => { if (document.visibilityState === 'visible') onActivity() }
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const
    events.forEach((name) => window.addEventListener(name, onActivity, { passive: true }))
    document.addEventListener('visibilitychange', onVisibility)
    arm()
    return () => {
      window.clearTimeout(timer)
      events.forEach((name) => window.removeEventListener(name, onActivity))
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled])
  const myShownStatus: ChatShownStatus = myStatus === 'AVAILABLE' && idle ? 'AWAY' : myStatus
  /* The server shows others Ausente only when every tab of mine is idle (F-184, `presence.idle`); a fresh
     socket starts as active, so after a reconnect only an idle tab needs to say so again. */
  const idleSentRef = React.useRef(false)
  const realtimeSend = realtime.send
  React.useEffect(() => {
    if (!connected) {
      idleSentRef.current = false
      return
    }
    if (idle === idleSentRef.current) return
    if (realtimeSend({ type: 'presence.idle', idle })) idleSentRef.current = idle
  }, [idle, connected, realtimeSend])
  const setChatStatus = React.useCallback(async (next: MyChatStatus) => {
    let previous: ChatMe | null = null
    setMe((m) => {
      previous = m
      return m ? { ...m, chatStatus: next, chatAvailable: next !== 'UNAVAILABLE', showUnreadBadge: next !== 'UNAVAILABLE' } : m
    })
    try {
      const updated = await sdk.chat.updateMe({ chatStatus: next, availabilityNoticeSeen: true })
      setMe((m) => ({ ...(m || {}), ...updated } as ChatMe))
    } catch (err) {
      setMe(previous)
      throw err
    }
  }, [])

  const show = React.useCallback((conversationId: string, opts: ShowOptions = {}) => {
    const wanted = opts.focus !== false
    if (wanted) requestFocus(conversationId, opts.text)
    setActiveIds((ids) => {
      /* Asked for by a person: it comes to the first slot (out of the "+N" menu if it was there). */
      if (ids.includes(conversationId)) return wanted && ids[0] !== conversationId ? [conversationId, ...ids.filter((x) => x !== conversationId)] : ids
      return [conversationId, ...ids].slice(0, MAX_DOCKED_WINDOWS)
    })
    /* Below 1024 px the conversation opens full screen; on desktop the docked windows show it. */
    if (window.innerWidth < 1024) setMobileOpen(true)
  }, [requestFocus])

  const upsertConversation = React.useCallback((conversation: ChatConversation) => {
    setConversations((list) => {
      const rest = list.filter((c) => c.id !== conversation.id)
      return [conversation, ...rest].sort(byLastActivity)
    })
  }, [])

  const openWith = React.useCallback(async (profileId: string, profile?: ChatDraft['profile'], connection?: ChatConnectionState, opts?: ShowOptions) => {
    const existing = conversationsRef.current.find((c) => c.other?.id === profileId)
    if (existing) {
      show(existing.id, opts)
      return existing.id
    }
    const draftId = DRAFT_PREFIX + profileId
    setDrafts((all) => ({
      ...all,
      [draftId]: { id: draftId, profile: { ...(all[draftId]?.profile || {}), ...(profile || {}), id: profileId }, connection: connection || all[draftId]?.connection || 'none' }
    }))
    show(draftId, opts)
    return draftId
  }, [show])

  const sendFirstMessage = React.useCallback(async (draftId: string, body: string) => {
    const profileId = draftId.slice(DRAFT_PREFIX.length)
    const conversation = await sdk.chat.openConversation(profileId)
    upsertConversation(conversation)
    setActiveIds((ids) => ids.map((id) => (id === draftId ? conversation.id : id)))
    setDrafts((all) => {
      const next = { ...all }
      delete next[draftId]
      return next
    })
    setMessages((all) => ({ ...all, [conversation.id]: all[conversation.id] || { items: [], hasMore: false, cursor: null, state: 'ready' } }))
    await sendMessageRef.current(conversation.id, body)
  }, [upsertConversation])

  const clearHistory = React.useCallback(async (conversationId: string) => {
    await sdk.chat.clearConversation(conversationId)
    setMessages((all) => ({ ...all, [conversationId]: { items: [], hasMore: false, cursor: null, state: 'ready' } }))
    setConversations((list) => list.map((c) => (c.id === conversationId ? { ...c, lastMessage: null, unreadCount: 0 } : c)))
  }, [])

  const showConversation = React.useCallback(async (conversationId: string, opts?: ShowOptions) => {
    const conversation = await sdk.chat.getConversation(conversationId)
    upsertConversation(conversation)
    show(conversation.id, opts)
  }, [show, upsertConversation])

  const showReturnedConversation = React.useCallback((conversation: ChatConversation, opts?: ShowOptions) => {
    upsertConversation(conversation)
    show(conversation.id, opts)
  }, [show, upsertConversation])

  const closeConversation = React.useCallback((conversationId: string) => {
    setActiveIds((ids) => ids.filter((id) => id !== conversationId))
  }, [])

  const loadMessages = React.useCallback(async (conversationId: string, opts: { older?: boolean; silent?: boolean } = {}) => {
    const current = messagesRef.current[conversationId]
    if (opts.older) {
      if (!current?.hasMore || current.loadingOlder || !current.cursor) return
      setMessages((all) => ({ ...all, [conversationId]: { ...all[conversationId], loadingOlder: true } }))
    } else if (!current || (!opts.silent && current.state === 'failed')) {
      setMessages((all) => ({ ...all, [conversationId]: { items: all[conversationId]?.items || [], hasMore: false, cursor: null, state: 'loading' } }))
    }
    try {
      const page = await sdk.chat.listMessages(conversationId, opts.older && current?.cursor ? { before: current.cursor, limit: 30 } : { limit: 30 })
      const items = page.items.map((m) => ({ ...m, mine: m.mine ?? m.senderId === myId }))
      setMessages((all) => {
        const previous = all[conversationId]
        const merged = mergeMessages(previous?.items || [], items)
        /* A refresh of the newest page keeps the cursor of what is already loaded further back. */
        const keepOlder = !opts.older && previous && previous.state === 'ready' && previous.items.length > items.length
        return {
          ...all,
          [conversationId]: {
            items: merged,
            hasMore: keepOlder ? previous.hasMore : Boolean(page.pagination?.hasMore),
            cursor: keepOlder ? previous.cursor : page.pagination?.nextCursor ?? items[0]?.id ?? null,
            state: 'ready',
            loadingOlder: false
          }
        }
      })
    } catch {
      setMessages((all) => ({
        ...all,
        [conversationId]: opts.older || all[conversationId]?.state === 'ready'
          ? { ...all[conversationId], loadingOlder: false }
          : { items: all[conversationId]?.items || [], hasMore: false, cursor: null, state: 'failed' }
      }))
    }
  }, [myId])

  const sendMessage = React.useCallback(async (conversationId: string, body: string, retryOf?: string) => {
    if (soundsOnRef.current) playSendSound()
    const tempId = retryOf || `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const draft: PendingMessage = {
      id: tempId,
      conversationId,
      senderId: myId || '',
      mine: true,
      body,
      createdAt: new Date().toISOString(),
      sendState: 'sending'
    }
    setMessages((all) => {
      const current = all[conversationId] || { items: [], hasMore: false, cursor: null, state: 'ready' as const }
      const items = retryOf
        ? current.items.map((m) => (m.id === retryOf ? draft : m))
        : [...current.items, draft]
      return { ...all, [conversationId]: { ...current, items } }
    })
    try {
      const sent = await sdk.chat.sendMessage(conversationId, body)
      const saved: PendingMessage = { ...sent, mine: true }
      setMessages((all) => {
        const current = all[conversationId]
        if (!current) return all
        const withoutDraft = current.items.filter((m) => m.id !== tempId)
        return { ...all, [conversationId]: { ...current, items: mergeMessages(withoutDraft, [saved]) } }
      })
      setConversations((list) => list.map((c) => (c.id === conversationId
        ? { ...c, lastMessage: saved, lastMessageAt: saved.createdAt }
        : c)).sort(byLastActivity))
      /* The server marks the conversation read for the sender: the badge takes its total. */
      meGateRef.current.invalidate()
      scheduleMeSync()
    } catch (err) {
      /* A limit (429) takes the bubble back out: the text returns to the composer with the API's message. */
      const limited = (err as { response?: { status?: number } })?.response?.status === 429
      setMessages((all) => {
        const current = all[conversationId]
        if (!current) return all
        if (limited) return { ...all, [conversationId]: { ...current, items: current.items.filter((m) => m.id !== tempId) } }
        return { ...all, [conversationId]: { ...current, items: current.items.map((m) => (m.id === tempId ? { ...m, sendState: 'failed' } : m)) } }
      })
      /* 403: blocked or the other person left; the conversation says so from now on. */
      if (statusOf(err) === 403) {
        sdk.chat.getConversation(conversationId).then(upsertConversation).catch(() => {})
      }
      throw err
    }
  }, [myId, upsertConversation, scheduleMeSync])

  const sendMessageRef = React.useRef(sendMessage)
  sendMessageRef.current = sendMessage

  const deleteMessage = React.useCallback(async (conversationId: string, messageId: string) => {
    await sdk.chat.deleteMessage(messageId)
    onMessageDeleted({ id: messageId, conversationId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sendTyping = React.useCallback((conversationId: string) => {
    if (!connected || !UUID.test(conversationId)) return
    const last = typingSentRef.current.get(conversationId) || 0
    if (Date.now() - last < TYPING_SEND_GAP_MS) return
    if (realtime.send({ type: 'typing', conversationId })) typingSentRef.current.set(conversationId, Date.now())
  }, [connected, realtime])

  const setBlocked = React.useCallback(async (profileId: string, blocked: boolean) => {
    if (blocked) await sdk.chat.block(profileId)
    else await sdk.chat.unblock(profileId)
    await refreshConversations()
    if (me?.chatAvailable) refreshOnline()
  }, [refreshConversations, refreshOnline, me?.chatAvailable])

  const registerVisible = React.useCallback((conversationId: string) => {
    visibleRef.current.set(conversationId, (visibleRef.current.get(conversationId) || 0) + 1)
    return () => {
      const count = (visibleRef.current.get(conversationId) || 1) - 1
      if (count <= 0) visibleRef.current.delete(conversationId)
      else visibleRef.current.set(conversationId, count)
    }
  }, [])

  const onlineIds = React.useMemo(() => new Set(online.map((p) => p.id)), [online])

  const isOnline = React.useCallback((profileId: string) => onlineIds.has(profileId), [onlineIds])

  const canMessage = React.useCallback((profileId: string, hint: { chatAvailable?: boolean | null } = {}) => {
    if (!enabled || !profileId || profileId === myId) return false
    const existing = conversations.find((c) => c.other?.id === profileId)
    const personAvailable = hint.chatAvailable === true || onlineIds.has(profileId) || existing?.other?.available === true
    if (!personAvailable) return false
    if (existing) return existing.canSend !== false
    return me?.canStart !== false
  }, [enabled, myId, conversations, onlineIds, me?.canStart])

  /* The tab title carries the unread total, only for someone available (showUnreadBadge). */
  const badgeCount = enabled && me?.showUnreadBadge !== false && me?.chatAvailable ? me?.unreadTotal || 0 : 0
  React.useEffect(() => {
    const prefix = /^\(\d+\+?\) /
    const apply = () => {
      const base = document.title.replace(prefix, '')
      const wanted = badgeCount > 0 ? `(${badgeCount > 99 ? '99+' : badgeCount}) ${base}` : base
      if (document.title !== wanted) document.title = wanted
    }
    apply()
    if (badgeCount <= 0) return
    /* Pages set their own titles as the route changes; the count is put back on each one. */
    const observer = new MutationObserver(apply)
    observer.observe(document.head, { childList: true, subtree: true, characterData: true })
    return () => {
      observer.disconnect()
      document.title = document.title.replace(prefix, '')
    }
  }, [badgeCount])

  const value: ChatContextValue = {
    status,
    enabled,
    me,
    myId,
    connected,
    conversations,
    conversationsState,
    conversationsHasMore,
    loadingMoreConversations,
    loadMoreConversations,
    online,
    onlineTotal,
    onlineState,
    panelState,
    setPanelState,
    activeIds,
    mobileOpen,
    setMobileOpen,
    sidePanelOpen,
    setSidePanelOpen,
    messages,
    typing,
    syncTick,
    refresh,
    refreshConversations,
    refreshOnline,
    updateMe,
    myStatus,
    myShownStatus,
    setChatStatus,
    openWith,
    drafts,
    sendFirstMessage,
    clearHistory,
    showConversation,
    showReturnedConversation,
    focusRequest,
    requestFocus,
    showWindow: show,
    soundsOn,
    setSoundsOn,
    reportAtBottom,
    closeConversation,
    loadMessages,
    sendMessage,
    deleteMessage,
    markRead,
    sendTyping,
    setBlocked,
    registerVisible,
    canMessage,
    isOnline
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

/** The unread total shown on the collapsed bar and the mobile header (0 when it must not show). */
export const useChatBadge = (): number => {
  const chat = useOptionalChat()
  if (!chat?.enabled || !chat.me?.chatAvailable || chat.me.showUnreadBadge === false) return 0
  return chat.me.unreadTotal || 0
}
